import type { LocationType } from '~/composables/types/strapiEventListPage'

export const LOCATION_SLUG_REGEX = /^(.+)--([srn])$/

export const TYPE_PREFIX_MAP: Record<string, LocationType> = {
  s: 'state',
  r: 'region',
  n: 'neighborhood',
}

export const LOCATION_TYPE_TO_PREFIX: Record<string, string> = {
  region: 'r',
  state: 's',
  neighborhood: 'n',
}

interface ParsedCitySlug {
  isCity: true
  locationSlug: string
  categorySlug: string
  readableName: string
  locationType: 'city'
  typePrefix: null
}

interface ParsedLocationSlug {
  isCity: false
  locationSlug: string
  categorySlug: string
  readableName: string
  locationType: 'state' | 'region' | 'neighborhood'
  typePrefix: string
}

export type ParsedSlug = ParsedCitySlug | ParsedLocationSlug

/**
 * Encodes a plain slug and location type into the composite URL format.
 * Cities use no suffix. Non-city types get a `--{prefix}` suffix.
 * e.g. encodeLocationTypeSlug('berlin', 'region') -> 'berlin--r'
 *      encodeLocationTypeSlug('berlin', 'city')   -> 'berlin'
 */
export const encodeLocationTypeSlug = (
  slug: string,
  locationType?: string
): string => {
  if (!locationType || locationType === 'city') return slug
  const prefix = LOCATION_TYPE_TO_PREFIX[locationType]
  return prefix ? `${slug}--${prefix}` : slug
}

/**
 * Pure parsing function — can be tested without Nuxt context.
 */
export const parseLocationSlug = (
  rawSlug: string,
  categorySlug: string = ''
): ParsedSlug => {
  const match = rawSlug.match(LOCATION_SLUG_REGEX)

  if (match) {
    const typePrefix = match[2]!
    return {
      isCity: false,
      locationSlug: rawSlug,
      categorySlug,
      readableName: match[1]!,
      locationType: TYPE_PREFIX_MAP[typePrefix] as
        | 'state'
        | 'region'
        | 'neighborhood',
      typePrefix,
    }
  }

  return {
    isCity: true,
    locationSlug: rawSlug,
    categorySlug,
    readableName: rawSlug,
    locationType: 'city',
    typePrefix: null,
  }
}

export const useLocationSlugParser = (): ParsedSlug => {
  const route = useRoute()
  return parseLocationSlug(
    route.params.location?.toString() ?? '',
    route.params.category?.toString() ?? ''
  )
}
